import * as React from 'react';
import { Dispatch, ReactNode, SetStateAction, createContext, useEffect, useState } from "react";
import { LoginRequest, LoginResponse, Receipt } from "../api/Models";
import { deleteStoreValue, getStoreValue, storeValue, catchError } from '../utils/utils';
import { SMDCAPI } from '../api/Api';
import { useNavigate } from 'react-router-dom';
import moment from "moment"

const ACCESS_TOKEN = 'smdc-jwt_access'
const REFRESH_TOKEN = 'smdc-jwt_refresh'
const USER_ID = 'smdc-jwt_userid'
const EXPIRES_AT = 'smdc-jwt_expiresAT';

export interface ApplicationContext {
    authState: { token: string | null; refreshToken: string | null | undefined, authenticated: boolean | null },
    setAuthState: Dispatch<SetStateAction<any>>,
    onLogin: (req: LoginRequest) => Promise<LoginResponse | any>,
    onLogout: () => Promise<any>,
    onGetTransactions: (
        start?: Date | null,
        end?: Date | null,
    ) => Promise<any>,
    onGetReceipt: (transactionID: Receipt) => Promise<any>,
    onGetReceiptReports: (transactionID: Receipt) => Promise<any>,
    onFilteredTransactions: (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null,
        properties?: string | null
    ) => Promise<any>
    onFilteredReports: (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null
    ) => Promise<any>,
    onGetAutomaticPayments: (
        start?: Date | null,
        end?: Date | null,
        searchTerm?: string | null,
        property?: string | null
    ) => Promise<any>,
    onGetAutomaticPaymentsReport: (
        start?: Date | null,
        end?: Date | null,
        searchTerm?: string | null,
        property?: string | null
    ) => Promise<any>,
    onGetPropertyNames: () => Promise<any>,
}

export const AppContext = createContext<ApplicationContext>({
    authState: {
        token: null,
        refreshToken: null,
        authenticated: null
    },
    setAuthState: () => { },
    onLogin: async (req: LoginRequest): Promise<LoginResponse | any> => { },
    onLogout: async (): Promise<any> => { },
    onGetTransactions: async (
        start?: Date | null,
        end?: Date | null,
    ): Promise<any> => { },
    onGetReceipt: async (transactionID: Receipt): Promise<any> => { },
    onFilteredTransactions: async (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null,
        properties?: string | null
    ): Promise<any> => { },
    onFilteredReports: async (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null
    ): Promise<any> => { },
    onGetReceiptReports: async (transactionID: Receipt): Promise<any> => { },
    onGetAutomaticPayments: async (
        start?: Date | null,
        end?: Date | null,
        searchTerm?: string | null,
        property?: string | null
    ): Promise<any> => { },
    onGetAutomaticPaymentsReport: async (
        start?: Date | null,
        end?: Date | null,
        searchTerm?: string | null,
        property?: string | null
    ): Promise<any> => { },
    onGetPropertyNames: async (): Promise<any> => { },
});

type AppContextProviderProps = {
    children: ReactNode;
};

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState<{
        token: string | null,
        refreshToken: string | null | undefined,
        authenticated: boolean | null,
        userId: string | null,
        expiresAt: string | null,
    }>({
        token: '',
        refreshToken: '',
        authenticated: false,
        userId: '',
        expiresAt: ''
    })

    const logout = React.useCallback(async () => {
        setAuthState({
            token: null,
            refreshToken: null,
            authenticated: null,
            userId: null,
            expiresAt: null
        })

        await deleteStoreValue(ACCESS_TOKEN)
        await deleteStoreValue(REFRESH_TOKEN)
        await deleteStoreValue(USER_ID)
        await deleteStoreValue(EXPIRES_AT)

        localStorage.clear()
        navigate('/login');
    }, [setAuthState, navigate]);

    const checkTokenValidity = React.useCallback(async (accessToken: any, expiresAt: any) => {
        if (!accessToken) {
            await logout();
        } else {
            if (!expiresAt) return
            const currentTime = Math.floor(Date.now() / 1000);
            const expires = Math.floor(Date.parse(expiresAt) / 1000);

            if (expires < currentTime) {
                await logout();
            }
        }
    }, [logout]);


    const loadToken = React.useCallback(async () => {
        const token = await getStoreValue(ACCESS_TOKEN);
        const refreshToken = await getStoreValue(REFRESH_TOKEN);
        const userId = await getStoreValue(USER_ID);
        const expiresAt = await getStoreValue(EXPIRES_AT);

        if (token) {
            setAuthState({
                token,
                refreshToken,
                authenticated: true,
                userId,
                expiresAt
            });

            if (!expiresAt) return

            const currentTime = Math.floor(Date.now() / 1000);
            const expires = Math.floor(Date.parse(expiresAt) / 1000);

            if (expires < currentTime) {
                await logout();
            }
        }
    }, [logout]);

    // START AUTH
    const login = async (request: LoginRequest) => {
        try {
            const response = await SMDCAPI.login(request) as LoginResponse
            setAuthState({
                token: response.token,
                refreshToken: response.refresh_token,
                authenticated: true,
                userId: response.id,
                expiresAt: response.expiresAt
            })

            await storeValue(ACCESS_TOKEN, response.token)
            await storeValue(REFRESH_TOKEN, response.refresh_token)
            await storeValue(USER_ID, response.id)
            await storeValue(EXPIRES_AT, response.expiresAt)

            localStorage.setItem('ACCESS_TOKEN', response.token)
            localStorage.setItem('REFRESH_TOKEN', response.refresh_token)
            localStorage.setItem('USER_ID', response.id)
            localStorage.setItem('EXPIRES_AT', response.expiresAt)

            return response
        } catch (error: any) {
            catchError(error)
        }
    }
    // END AUTH

    const getTransactions = async (
        startDate: Date | null,
        endDate: Date | null
    ) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        const start = startDate
        const end = endDate

        try {
            const response = await SMDCAPI.getTransactions(accessToken, start, end);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const getReceipt = async (paymentID: Receipt) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SMDCAPI.getReceipt(accessToken, paymentID);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const getReceiptReports = async (paymentID: Receipt) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SMDCAPI.getReceiptReports(accessToken, paymentID);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const getAutomaticPayments = async (
        startDate: Date | null,
        endDate: Date | null,
        searchTerm: string | null,
        property: string | null
    ) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        const start = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

        try {
            const response = await SMDCAPI.getAutomaticPayments(accessToken, start, end, searchTerm, property);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const getAutomaticPaymentsReport = async (
        startDate: Date | null,
        endDate: Date | null,
        searchTerm: string | null,
        property: string | null
    ) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        const start = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

        try {
            const response = await SMDCAPI.getAutomaticPaymentsReport(accessToken, start, end, searchTerm, property);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const onFilterTrsnctn = async (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null,
        properties?: string | null
    ) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        const filters = {
            type: type,
            start: start,
            end: end,
            searchValue: searchValue,
            properties: properties
        }

        try {
            const response = await SMDCAPI.getFilteredTransactions(filters, accessToken);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const onFilterReports = async (
        type?: string | null,
        start?: Date | null,
        end?: Date | null,
        searchValue?: string | null
    ) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        const filters = {
            type: type,
            start: start,
            end: end,
            searchValue: searchValue
        }

        try {
            const response = await SMDCAPI.getFilteredReports(filters, accessToken);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    const getPropertyNames = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SMDCAPI.getProperyNames(accessToken);
            return response;
        } catch (error) {
            catchError(error)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            const accessToken = await getStoreValue(ACCESS_TOKEN);
            const expiresAt = localStorage.getItem('EXPIRES_AT')

            await loadToken();
            await checkTokenValidity(accessToken, expiresAt);
        };

        fetchData();
    }, [loadToken, checkTokenValidity]);

    const globalData = {
        authState,
        setAuthState,
        onLogin: login,
        onLogout: logout,
        onGetTransactions: getTransactions,
        onGetReceipt: getReceipt,
        onFilteredTransactions: onFilterTrsnctn,
        onFilteredReports: onFilterReports,
        onGetReceiptReports: getReceiptReports,
        onGetAutomaticPayments: getAutomaticPayments,
        onGetAutomaticPaymentsReport: getAutomaticPaymentsReport,
        onGetPropertyNames: getPropertyNames,
    };

    return (
        <AppContext.Provider value={globalData}>
            {children}
        </AppContext.Provider>
    )
}